import paymentsService from '@/modules/payments/services/paymentsService';
import SalesWidgetStore from '@/modules/widgets/salesWidget/store/salesWidgetStore';
import PaymentPlansStore from '@/modules/payments/store/paymentPlansStore';

const LINKED_PAYMENT_PLANS_FEATURE = 'rollout.payments.payables_linked_payment_plans';

// v3 payment_plans replaces v1 scheduled_payments_rules while this flag rolls out.
// TODO: (VCITA2-15351 - flag cleanup): drop this and call the plan methods unconditionally.
const usePaymentPlansV3 = (rootGetters) => rootGetters['BusinessStore/hasFeature'](LINKED_PAYMENT_PLANS_FEATURE);

const state = {
  scheduledPayments: {},
  payments: {},
  paymentStatuses: {},
};

const getters = {
  scheduledPayments: (state) => state.scheduledPayments,
  scheduledPaymentsByUid: (state) => (uid) => state.scheduledPayments[uid],
  paymentsByScheduledPaymentsUid: (state) => (uid) => Object.values(state.payments)
    .filter((payment) => payment.scheduled_payments_rule_uid === uid),
  paymentsByPaymentStatusUid: (state) => (uid) => Object.values(state.payments)
    .filter((payment) => payment.payment_request_uid === uid),
  paymentByUid: (state) => (uid) => state.payments[uid],
  paymentStatusByUid: (state) => (uid) => state.paymentStatuses[uid],
};

const mutations = {
  setScheduledPayments(state, scheduledPayments) {
    state.scheduledPayments = {
      ...state.scheduledPayments,
      [scheduledPayments.uid]: scheduledPayments,
    };
  },
  setPayments(state, payments) {
    payments.forEach((payment) => {
      state.payments = { ...state.payments, [payment.uid]: payment };
    });
  },
  setPaymentStatus(state, paymentStatus) {
    state.paymentStatuses = {
      ...state.paymentStatuses,
      [paymentStatus.uid]: paymentStatus,
    };
  },
  updatePaymentStatus(state, updatedFields) {
    state.paymentStatuses = {
      ...state.paymentStatuses,
      [updatedFields.uid]: {
        ...state.paymentStatuses[updatedFields.uid],
        ...updatedFields,
      },
    };
  },
};

const actions = {
  async getScheduledPayments({ commit, rootGetters }, uid) {
    const scheduledPayments = usePaymentPlansV3(rootGetters)
      ? await paymentsService.fetchPaymentPlan(uid)
      : await paymentsService.fetchScheduledPaymentsV1(uid);
    commit('setScheduledPayments', scheduledPayments);
  },
  async getPaymentsByScheduledPaymentsUid({ commit }, scheduledPaymentsUid) {
    const payments = await paymentsService.fetchPaymentsByScheduledPaymentsUid(scheduledPaymentsUid);
    commit('setPayments', [...payments]);
  },
  async getPaymentsByPaymentStatusUid({ commit }, paymentStatusUid) {
    const payments = await paymentsService.fetchPaymentsByPaymentStatusUid(paymentStatusUid);
    commit('setPayments', [...payments]);
  },
  // Always refetched: the balance moves whenever a payment lands, and waivePayment writes partial
  // fields into the same map, so a cached entry is neither current nor necessarily complete.
  async getPaymentRequest({ commit }, uid) {
    const paymentStatus = await paymentsService.fetchPaymentRequest(uid);
    commit('setPaymentStatus', paymentStatus);
  },
  async cancelScheduledPayments({ commit, rootGetters }, scheduledPaymentsUid) {
    const scheduledPayments = usePaymentPlansV3(rootGetters)
      ? await paymentsService.cancelPaymentPlan(scheduledPaymentsUid)
      : await paymentsService.cancelScheduledPaymentsV1(scheduledPaymentsUid);
    commit('setScheduledPayments', scheduledPayments);
  },
  // eslint-disable-next-line no-shadow
  async fetchPayment({ commit, getters }, { uid, forceFetch = true }) {
    const existingPayment = getters.paymentByUid(uid);
    if (!existingPayment || forceFetch) {
      const payment = await paymentsService.fetchPayment(uid);
      commit('setPayments', [payment]);
    }
  },
  async refundPayment({ dispatch }, payload) {
    const response = await paymentsService.refundPayment(payload);
    dispatch('fetchPayment', { uid: payload.refund.payment_uid });
    return response;
  },
  async waivePayment({ commit }, { paymentStatusUid, payload }) {
    const waivePaymentResponse = await paymentsService.waivePayment(paymentStatusUid, payload);
    commit('updatePaymentStatus', waivePaymentResponse.data);
    return waivePaymentResponse;
  },
};

const modules = {
  SalesWidgetStore,
  PaymentPlansStore,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules,
};
